import type { BugSeverity, WorkItemStatus, WorkItemSummary } from "../../shared/types/api";

/**
 * The severities, worst first — the order of the filter and the strip.
 *
 * <p>The list itself is ordered by the server, which is the only place that can see past the ten
 * rows a page holds. This exists so the two screens offer the severities in the same order, not
 * to sort anything.
 */
export const SEVERITY_ORDER: BugSeverity[] = [
  "BLOCKER",
  "CRITICAL",
  "MAJOR",
  "MINOR",
  "TRIVIAL",
];

/** Bugs nobody has finished with. The default view, because closed bugs are not work. */
const CLOSED: WorkItemStatus[] = ["DONE", "CANCELLED"];

export function isOpen(bug: WorkItemSummary): boolean {
  return !CLOSED.includes(bug.status);
}

/**
 * How many days a bug has been open, or null when it has no detected date.
 *
 * <p>Counted from the day it was found rather than from the day it was raised in here: a bug
 * found on Friday and typed up on Monday has been costing somebody three days either way.
 */
export function daysOpen(detectedDate: string | null | undefined, today: string): number | null {
  if (!detectedDate) {
    return null;
  }
  const from = Date.parse(`${detectedDate}T00:00:00`);
  const to = Date.parse(`${today}T00:00:00`);
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return null;
  }
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

/** "6 days", "today", or an em dash when nothing says when it was found. */
export function formatAge(days: number | null): string {
  if (days == null) {
    return "—";
  }
  if (days === 0) {
    return "today";
  }
  return `${days} day${days === 1 ? "" : "s"}`;
}
